import React from "react";
import { useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { editCompanySchema, type EditCompanyForm } from "@kkp/features";
import { toast } from "@kkp/ui";
import { updateCompany, type Company } from "../../../api/companies";

/**
 * Edit-company dialog form: owns the dialog open state, the company being edited,
 * the react-hook-form wiring, and the (partial) update submission. `openEditDialog`
 * seeds the form + opens the dialog; on success it hands the updated company back
 * via `onUpdated` (the data hook replaces it in the list).
 */
export function useEditCompanyForm({
  onUpdated,
}: {
  onUpdated: (updated: Company) => void;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [editingCompany, setEditingCompany] = React.useState<Company | null>(
    null,
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<EditCompanyForm>({
    // zod 4 types a z.preprocess input as `unknown`, which @hookform/resolvers
    // v5 now surfaces on the resolver's input type (v4 keyed only on the output).
    // The runtime behaviour is unchanged; cast to the form's output resolver.
    resolver: zodResolver(
      editCompanySchema,
    ) as unknown as Resolver<EditCompanyForm>,
    defaultValues: {
      legalName: "",
      backOfficeId: "",
      taxId: "",
      legalAddress: "",
      sourceBankCode: "",
      sourceBankAccount: "",
      perTransactionFeeIdr: "",
      picFullname: "",
      picPhonenumber: "",
    },
  });

  const openEditDialog = (company: Company) => {
    setEditingCompany(company);
    setValue("legalName", company.name, { shouldDirty: false });
    setValue("backOfficeId", "", { shouldDirty: false });
    setValue("taxId", "", { shouldDirty: false });
    setValue("legalAddress", "", { shouldDirty: false });
    setValue("sourceBankCode", "", { shouldDirty: false });
    setValue("sourceBankAccount", "", { shouldDirty: false });
    setValue("perTransactionFeeIdr", "", { shouldDirty: false });
    setValue("picFullname", "", { shouldDirty: false });
    setValue("picPhonenumber", "", { shouldDirty: false });
    setIsOpen(true);
  };

  const cancel = () => {
    setIsOpen(false);
    setEditingCompany(null);
    reset();
  };

  const onSubmit = handleSubmit(async (values) => {
    if (!editingCompany) return;
    const payload = {
      ...(values.legalName ? { companyLegalName: values.legalName } : {}),
      ...(values.backOfficeId ? { backOfficeId: values.backOfficeId } : {}),
      ...(values.taxId ? { taxId: values.taxId } : {}),
      ...(values.legalAddress ? { legalAddress: values.legalAddress } : {}),
      ...(values.sourceBankCode
        ? { sourceBankCode: values.sourceBankCode }
        : {}),
      ...(values.sourceBankAccount
        ? { sourceBankAccount: values.sourceBankAccount }
        : {}),
      ...(values.perTransactionFeeIdr
        ? { perTransactionFeeIdr: values.perTransactionFeeIdr }
        : {}),
      ...(values.picFullname ? { picFullname: values.picFullname } : {}),
      ...(values.picPhonenumber
        ? { picPhonenumber: values.picPhonenumber }
        : {}),
    };

    const updated = await updateCompany(editingCompany.id, payload);
    onUpdated(updated);
    toast.success("Company updated.", { title: "Success", durationMs: 2200 });
    setIsOpen(false);
    setEditingCompany(null);
    reset();
  });

  return {
    isOpen,
    setIsOpen,
    editingCompany,
    openEditDialog,
    cancel,
    register,
    errors,
    isSubmitting,
    onSubmit,
  };
}
